import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import { usePDPData } from '~/features/product-details/context';
import { getRuntimeConfig } from '~/shared/utils/config';
import { useEvents } from '~/shared/hooks/useEvents';
import { transformSalesforceProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { useGABreadcrumb } from '~/shared/hooks/useGABreadcrumb';
import { useFrame } from '~/shared/utils';
import WishCloudIcon from '~/icons/wishcloud.svg';
import styles from './WishCloudButton.module.scss';

interface Props extends Omit<React.HTMLAttributes<HTMLButtonElement>, 'onClick' | 'id'> {
    instanceId?: string | number;
}

let instanceCounter = 0;

export const WishCloudButton: React.FC<Props> = ({ instanceId, className, ...rest }) => {
    // Held in a ref so a re-render never hands the button a different id.
    const assignedInstanceId = useRef<string | number | null>(null);
    const { data: frame } = useFrame();
    const { clickWishCloudButtonEvent, errorEventWishCloud } = useEvents(frame);
    const gaBreadcrumb = useGABreadcrumb();
    const { product } = usePDPData();
    const { config } = getRuntimeConfig();

    if (assignedInstanceId.current === null) {
        assignedInstanceId.current = instanceId ?? instanceCounter++;
    }

    useEffect(() => {
        const handleWishCloudError = (event: ErrorEvent) => {
            // Safari blocks the GoWish iframe cross-origin; the failure only surfaces here.
            if (
                event.error?.name === 'SecurityError' ||
                event.message?.includes('cross-origin frame') ||
                event.message?.includes('cross-origin') ||
                event.message?.includes('Blocked a frame with origin') ||
                event.message?.includes('frame with origin') ||
                event.message?.includes('protocols, domains, and ports must match') ||
                event.message?.includes('protocols domains ports must match') ||
                event.filename?.includes('gowish-iframe.js') ||
                event.filename?.includes('gowish')
            ) {
                console.log('WishCloud Safari security error detected:', event);
                errorEventWishCloud();
            }
        };

        window.addEventListener('error', handleWishCloudError);

        return () => {
            window.removeEventListener('error', handleWishCloudError);
        };
    }, [errorEventWishCloud]);

    const productUrl = useMemo(() => {
        if (!product?.c_pageURL || !config.NEXT_PUBLIC_URL) return undefined;

        try {
            return new URL(product.c_pageURL, config.NEXT_PUBLIC_URL).toString();
        } catch {
            return undefined;
        }
    }, [product?.c_pageURL]);

    const onClick = () => {
        clickWishCloudButtonEvent(transformSalesforceProductToDataLayer(product), gaBreadcrumb);
    };

    return (
        <button
            id={`gowishRoundedButton${assignedInstanceId.current}`}
            aria-label="Tilføj produkt til Ønskeskyen"
            className={clsx('gowish-btn-rounded', styles.wishCloudButton, className)}
            type="button"
            data-producturl={productUrl}
            title="Ønskeskyen"
            onClick={onClick}
            {...rest}
        >
            <WishCloudIcon className={styles.wishCloudIcon} aria-hidden="true" />
        </button>
    );
};
